import { ChangeDetectorRef, Component, ElementRef, NgZone, ViewChild, OnInit } from '@angular/core';
import * as XLSX from 'xlsx';
import { CostReportService } from '../services/cost-upload-report.service';
import { finalize, Subscription } from 'rxjs';

interface DefaultCostRow {
  DL: string;
  Location: string;
  Cost: number;
}

interface RowValidationError {
  rowNumber: number;
  DL: string;
  Location: string;
  issues: string[];
}

@Component({
  selector: 'app-default-cost-upload',
  templateUrl: './default-cost-upload.component.html',
  styleUrl: './default-cost-upload.component.scss',
  standalone: false
})
export class DefaultCostUploadComponent implements OnInit {

  @ViewChild('nativeCostFileInput') nativeFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('uploadPassInput') uploadPassInput?: ElementRef<HTMLInputElement>;
  fetchReportDataSubscription?: Subscription;

  file: File | null = null;
  previewRows: DefaultCostRow[] = [];

  isUploading = false;
  isSavingPass = false;
  showUploadPass = false;

  errorMessage = '';
  successMessage = '';

  isFileStructValid = true;
  structureIssues: string[] = [];
  rowValidationErrors: RowValidationError[] = [];

  missingUsers: any[] = [];
  lastUpdatedOn = '';
  private _successTimer: any = null;

  readonly REQUIRED_COLUMNS = ['DL', 'Location', 'Cost'];

  constructor(
    private ngZone: NgZone,
    private costReportService: CostReportService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.fetchLastUpdatedTimestamp();
  }

  ngOnDestroy(): void {
    this.fetchReportDataSubscription?.unsubscribe();
  }

  /* ============================
     File picker helpers
  ============================ */

  openNativeFileDialog(): void {
    this.nativeFileInput.nativeElement.click();
  }

  onClearClick(): void {
    this.clearAllMessages();
    this.resetUploadForm();
  }

  /* ============================
     File selection
  ============================ */

  onFileSelected(event: Event): void {
    // Clear old messages + old validation only when a NEW file is selected
    this.clearAllMessages();
    this.clearValidationState();

    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const selectedFile = input.files[0];
    const ext = selectedFile.name.split('.').pop()?.toLowerCase();

    if (!ext || !['xls', 'xlsx'].includes(ext)) {
      this.file = null;
      this.previewRows = [];
      this.setError('Invalid file format. Upload .xls or .xlsx only.');
      return;
    }

    this.file = selectedFile;
    this.readExcelAndConvertToJson(selectedFile);
  }

  /* ============================
     Excel → JSON + Validation
  ============================ */

  private readExcelAndConvertToJson(file: File): void {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];

        const jsonData = XLSX.utils.sheet_to_json<any>(worksheet, {
          defval: '',
          raw: true,
        });

        if (!jsonData.length) {
          this.previewRows = [];
          this.failStructure(['Excel sheet contains no rows.']);
          return;
        }

        this.validateColumns(Object.keys(jsonData[0]));
        if (!this.isFileStructValid) {
          this.previewRows = [];
          return;
        }

        const mappedRows: DefaultCostRow[] = jsonData.map(row => ({
          DL: String(row.DL ?? '').trim(),
          Location: String(row.Location ?? '').trim(),
          Cost: row.Cost,
        }));

        const errors = this.validateRowData(mappedRows);

        if (errors.length > 0) {
          this.rowValidationErrors = errors;
          this.isFileStructValid = false;
          this.errorMessage = `${errors.length} row(s) have missing required data. Please fix and re-upload.`;
          this.previewRows = [];
        } else {
          this.rowValidationErrors = [];
          this.isFileStructValid = true;
          this.errorMessage = '';
          this.previewRows = mappedRows;
        }

        this.cdr.markForCheck();
      } catch (err) {
        console.error(err);
        this.previewRows = [];
        this.failStructure(['Unable to read Excel file.']);
      }
    };

    reader.readAsArrayBuffer(file);
  }

  /* ============================
     Row-level data validation
  ============================ */

  private validateRowData(rows: DefaultCostRow[]): RowValidationError[] {
    const errors: RowValidationError[] = [];

    rows.forEach((row, index) => {
      const issues: string[] = [];
      const rowNumber = index + 2;

      const dlVal = String(row.DL ?? '').trim();
      const locVal = String(row.Location ?? '').trim();

      if (!dlVal) issues.push('DL is missing');
      if (!locVal) issues.push('Location is missing');

      if (issues.length > 0) {
        errors.push({
          rowNumber,
          DL: dlVal || '—',
          Location: locVal || '—',
          issues
        });
      }
    });

    return errors;
  }

  private validateColumns(headers: string[]): void {
    this.structureIssues = [];

    this.REQUIRED_COLUMNS.forEach(col => {
      if (!headers.includes(col)) {
        this.structureIssues.push(`Missing required column: ${col}`);
      }
    });

    this.isFileStructValid = this.structureIssues.length === 0;

    if (!this.isFileStructValid) {
      this.errorMessage = 'Excel file structure is invalid.';
    }
  }

  private failStructure(issues: string[]): void {
    this.structureIssues = issues;
    this.isFileStructValid = false;
    this.errorMessage = 'Excel file structure is invalid.';
  }

  /* ============================
     Upload to API
  ============================ */

  uploadToServer(): void {
    // Only clear success/error text, DO NOT clear validation table
    this.clearMessagesOnly();

    const pass = this.uploadPassInput?.nativeElement.value.trim() || '';

    if (!this.file) {
      this.setError('Please select a file.');
      return;
    }

    if (!pass) {
      this.setError('Please enter the passphrase.');
      return;
    }

    if (!this.isFileStructValid || this.rowValidationErrors.length > 0) {
      this.setError('Please fix all validation errors before uploading.');
      return;
    }

    this.isUploading = true;

    this.fetchReportDataSubscription = this.costReportService
      .insertDefaultCostReportData(pass, JSON.stringify(this.previewRows))
      .pipe(finalize(() =>
        this.ngZone.run(() => {
          this.isUploading = false;
          this.cdr.markForCheck();
        })
      ))
      .subscribe({
        next: (res: any) => this.ngZone.run(() => {
          if (res !== null && res.Message.recordset[0]?.Message !== 'Success') {
            this.errorMessage = res.Message.recordset[0].Message;
          } else {
            this.resetUploadForm();
            this.showSuccess('Upload completed successfully.', 8000);
            this.fetchLastUpdatedTimestamp();
          }
          this.cdr.markForCheck();
        }),
        error: (err) => this.ngZone.run(() => {
          console.error(err);
          this.errorMessage = 'Upload failed. Please try again.';
          this.cdr.markForCheck();
        })
      });
  }

  private resetUploadForm(): void {
    this.file = null;
    this.previewRows = [];
    this.showUploadPass = false;
    this.isFileStructValid = true;
    this.structureIssues = [];
    this.rowValidationErrors = [];

    if (this.uploadPassInput?.nativeElement) {
      this.uploadPassInput.nativeElement.value = '';
    }

    if (this.nativeFileInput?.nativeElement) {
      this.nativeFileInput.nativeElement.value = '';
    }
  }

  /* ============================
     Utilities
  ============================ */

  toggle(type: 'upload'): void {
    if (type === 'upload') {
      this.showUploadPass = !this.showUploadPass;
    }
  }

  // Clears only top messages
  private clearMessagesOnly(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }

  // Clears validation state only when new file is chosen or Clear is clicked
  private clearValidationState(): void {
    this.structureIssues = [];
    this.rowValidationErrors = [];
    this.isFileStructValid = true;
  }

  private clearAllMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }

  private setError(msg: string): void {
    this.errorMessage = msg;
  }

  private showSuccess(message: string, ms = 8000): void {
    this.successMessage = message;
    clearTimeout(this._successTimer);
    this._successTimer = setTimeout(() => this.successMessage = '', ms);
  }

  private fetchLastUpdatedTimestamp(): void {
    this.costReportService.getLastUpdatedOn('default-cost').subscribe({
      next: (response: any) => {
        if (response[0][0]?.lastUpdatedOn) {
          this.lastUpdatedOn = new Date(response[0][0].lastUpdatedOn).toLocaleString();
        }
      },
      error: () => {
        this.lastUpdatedOn = 'N/A';
      }
    });
  }
}